import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useDash } from "./ctx";
import { ClipboardCheck } from "lucide-react";
import { Visits } from "@/components/checklists/Visits";
import { Builder } from "@/components/checklists/Builder";
import { Settings } from "@/components/checklists/Settings";

export default function Checklists() {
  const { tenantId, role } = useDash();
  const readOnly = role === "board_member";
  const [templates, setTemplates] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [config, setConfig] = useState(null);
  const [today, setToday] = useState("");   // the community's local date, from the server
  const [view, setView] = useState("visits");
  const [openId, setOpenId] = useState(null);
  const [params, setParams] = useSearchParams();

  const loadVendors = useCallback(
    () => api.get(`/tenants/${tenantId}/checklists/recipients`)
      .then((r) => setVendors(r.data.vendors || [])).catch(() => {}),
    [tenantId]);

  const load = useCallback(async () => {
    const [t, cfg] = await Promise.all([
      api.get(`/tenants/${tenantId}/checklists`).then((r) => r.data.checklists || []).catch(() => []),
      api.get(`/tenants/${tenantId}/checklists/config`).then((r) => r.data.config).catch(() => null),
    ]);
    setTemplates(t); setConfig(cfg);
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    loadVendors();
    api.get(`/tenants/${tenantId}/checklists/visits`, { params: { days: 1, limit: 1 } })
      .then((r) => setToday(r.data.today || "")).catch(() => {});
  }, [tenantId, loadVendors]);
  useEffect(() => {
    const id = params.get("visit");
    if (id) { setOpenId(id); setView("visits"); }
  }, [params]);

  const openVisit = useCallback((id) => {
    setOpenId(id);
    // The URL carries the open visit so a manager can copy the link, and Back behaves.
    setParams(id ? { visit: id } : {});
  }, [setParams]);

  return (
    <div className="space-y-6">
      <PageTitle title="Checklists" about={<><ClipboardCheck className="-mt-0.5 mr-1.5 inline h-4 w-4 text-redline-500" />
        The recurring work you pay vendors for — cleaning, pool, landscaping, monthly walks. Every scheduled visit
        becomes a record whether or not anyone shows up, with tick times, photos and a conversation you can point a board at.</>} />

      <div className="flex flex-wrap items-center gap-2">
        {[["visits", "Visits"], ["builder", "Checklists"], ["settings", "Settings"]].map(([k, l]) => (
          <button key={k} onClick={() => { setView(k); setOpenId(null); setParams({}); }} data-testid={`cl-tab-${k}`}
            className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
              view === k ? "border-ink bg-ink text-white" : "border-sand-300 bg-white text-ink-700 hover:border-sand-400"}`}>
            {l}
          </button>
        ))}
      </div>

      {view === "settings" ? (
        <Settings tenantId={tenantId} config={config} readOnly={readOnly} onSaved={setConfig} />
      ) : view === "builder" ? (
        <Builder tenantId={tenantId} templates={templates} vendors={vendors} config={config}
          onSaved={load} onVendorAdded={loadVendors} readOnly={readOnly} today={today} />
      ) : (
        <Visits tenantId={tenantId} templates={templates} vendors={vendors} config={config}
          readOnly={readOnly} openId={openId} onOpen={openVisit} />
      )}
    </div>
  );
}
